import { BaseText, Meta, theme } from 'aimo-ui';
import CrickleCutCardBackground from 'components/common/CrickleCutCardBackground';
import useLoadFreePoolingSpots from 'hooks/useLoadFreePoolingSpots';
import { camelCase } from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AccessPermitAvailability from 'screens/permit/access/AccessPermitAvailability';
import PermitTicketHeader from 'screens/permit/PermitTicketHeader';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  AccessPermitPoolingGroupCombinedInfo,
  Maybe,
  UnifiedPermit,
} from 'types/generatedSchemaTypes';
import { getPoolingPaymentInfoText } from 'utils/permitUtils';

interface AccessPermitTicketProps {
  permit: UnifiedPermit;
}

interface BelowCutViewProps extends AccessPermitTicketProps {
  poolingGroup: Maybe<AccessPermitPoolingGroupCombinedInfo> | undefined;
  loading: any;
  data: any;
}

const BelowCutView: React.FC<BelowCutViewProps> = ({
  permit,
  poolingGroup,
  loading,
  data,
}) => {
  if (
    permit.permitType === 'ACCESS' ||
    permit.permitType === 'ACCESS_TO_MEMBER_GROUP_LTP' ||
    permit.permitType === 'DIGITAL_PERMIT'
  ) {
    return (
      <AccessPermitAvailability
        loading={loading}
        validity={permit.validity}
        availability={data?.getPoolingGroupCapacity?.free}
        isPooling={!!poolingGroup?.uid}
        capacityHidden={poolingGroup?.capacityHidden || false}
        parkingSpaces={permit.resolvedParkingSpaces}
        permitId={permit.id}
        spotMap={permit.spotMap}
        permitType={permit.permitType}
        freetextParkingSpaceNo={permit.freetextParkingSpaceNo}
      />
    );
  } else {
    return <></>;
  }
};

const AccessPermitTicket: React.FC<AccessPermitTicketProps> = ({ permit }) => {
  const { t } = useTranslation();
  const locationName = permit.placeName;

  // We can take just the first pool, as this component is for the single pool case.
  const poolingGroup = permit.accessPermitPoolingGroupInfo?.[0] ?? undefined;
  const poolingGroupUid = poolingGroup?.uid ?? undefined;

  const { loadFreePoolingSpots, loading, data } =
    useLoadFreePoolingSpots(poolingGroupUid);

  const customerUsage =
    permit?.usageType === 'CUSTOMER' ? t('permit.customerUsage') : '';

  useEffect(() => {
    if (permit.permitType === 'ACCESS') {
      loadFreePoolingSpots();
    }
  }, [loadFreePoolingSpots, permit.permitType]);

  let subtitle: string | undefined;
  if (
    permit.permitType === 'ACCESS' &&
    permit.accessPermitPoolingGroupInfo?.length === 1
  ) {
    subtitle = permit.accessPermitPoolingGroupInfo[0]?.pmcName || undefined;
  } else if (
    permit.permitType === 'ACCESS_TO_MEMBER_GROUP_LTP' ||
    permit.permitType === 'DIGITAL_PERMIT'
  ) {
    subtitle = permit.pmcName || undefined;
  }

  return (
    <CrickleCutCardBackground
      cutColor={theme.colors.lightBg.lilac}
      aboveCutView={() => (
        <>
          <PermitTicketHeader
            title={t(
              `permit.${camelCase(permit?.permitType?.toString())}.name`,
              ''
            )}
            subtitle={subtitle}
            testID="access-permit"
          />

          {permit?.parkingFacilityName && (
            <StyledWrapper mb={20} mt={10}>
              <BaseText center>{permit?.parkingFacilityName}</BaseText>
            </StyledWrapper>
          )}

          {locationName && locationName !== permit?.parkingFacilityName && (
            <StyledWrapper mb={20} mt={10}>
              <BaseText center>{locationName}</BaseText>
            </StyledWrapper>
          )}

          <StyledWrapper ml={20} mr={20} mb={20} mt={10}>
            <Meta center>{getPoolingPaymentInfoText(permit)}</Meta>
          </StyledWrapper>

          {customerUsage && (
            <Meta testID={`access-permit-${permit?.id}-usage`} center>
              {customerUsage}
            </Meta>
          )}
        </>
      )}
      belowCutView={() => (
        <BelowCutView
          permit={permit}
          loading={loading}
          data={data}
          poolingGroup={poolingGroup}
        />
      )}
      backgroundColor={theme.colors.lightBg.lilac}
    />
  );
};

export default AccessPermitTicket;
